/**
 * Second Shower article frame v1: the optional half.
 * Plan: 00-os/plans/geo-article-conversion.md, Layer 1. Styles: ss-article.css.
 *
 * The article reads correctly without this file. It adds, in this order:
 *   1. repeats folded (a lede that says what the next sentence says, a
 *      footnote printed five times, a buy link standing alone), the answer
 *      block split into answer and key points, weight put on keywords only
 *   2. contents: desktop rail, in-page disclosure, and the dock's sheet
 *   3. comparisons: ours marked and shown with its picture and the wordmark,
 *      rivals as text only (no rival product photo anywhere, 2026-10-02); on
 *      phones a wide table becomes one labelled card per product with its own
 *      position bar
 *   4. old FAQs (heading and paragraph) upgraded to the accordion
 *   5. the reading dock: progress, contents, the product
 *
 * It does not rewrite sentences, headings, link targets or structured data.
 * What it hides stays in the document.
 */
(function () {
  'use strict';

  var root = document.querySelector('.ssa');
  if (!root) return;
  var body = root.querySelector('.ssa-prose article.ss-article');
  if (!body) return;

  // Analytics: name where each body link sits, for ss_cta_click (assets/ss-track.js sends data-ss-cta as cta_id).
  // The frame's own links (rail, close card, dock, next reads) carry the attribute in snippets/ss-article-frame.liquid.
  body.querySelectorAll('a[href]:not([data-ss-cta])').forEach(function (a) {
    a.setAttribute('data-ss-cta', a.closest('.ss-tldr') ? 'article_tldr'
      : a.closest('table') ? 'article_table'
      : a.closest('.ss-router') ? 'article_router' : 'article_body');
  });

  // Phones and tablets: the frame's product line goes right after the answer box (hidden at 1100px+, where the rail shows).
  var pickInline = root.querySelector('.ssa-pick-inline');
  var answerBox = body.querySelector('.ss-tldr');
  if (pickInline && answerBox) {
    if (answerBox.nextElementSibling && answerBox.nextElementSibling.matches('.ss-scorecard')) answerBox = answerBox.nextElementSibling;
    answerBox.parentNode.insertBefore(pickInline, answerBox.nextSibling);
    pickInline.hidden = false;
  }

  var txt = function (el) { return (el.textContent || '').replace(/\s+/g, ' ').trim(); };
  var norm = function (s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); };
  var el = function (tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };
  var esc = function (s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
  var OURS = /second shower|showerhand|^\s*showerhead\s*$/i;

  var calm = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // What the theme hands over: the wordmark, our two product images, and the
  // rival brand names (alias -> "", names only).
  var WORDMARK = root.getAttribute('data-wordmark') || '';
  var BRANDS = {};
  try { BRANDS = JSON.parse((document.getElementById('ssa-brands') || {}).textContent || '{}'); } catch (e) { BRANDS = {}; }

  // Rival product photos the older bodies carry (files on our CDN named
  // "<brand>-...product..."): not shown, after a trademark complaint
  // (2026-10-02). Each goes with its frame before anything is laid out, so no
  // empty tile is left and nothing below picks it up.
  var RIVAL_IMG = /\/(afina|aquabliss|aquahomegroup|canopy|cobbe|eskiin|helloklean|jolie|mdhair|myhalos|sproos|weddell)-[^\/?#\s]*product/i;
  body.querySelectorAll('img').forEach(function (img) {
    var src = [img.getAttribute('src'), img.getAttribute('srcset'), img.getAttribute('data-src')].join(' ');
    if (!RIVAL_IMG.test(src)) return;
    var frame = img.closest('.ss-th-img, .ss-dive-media, .ss-compare-card, .ss-inline-figure, figure');
    if (frame && frame.contains(body)) frame = null;
    if (frame) frame.classList.add('ssa-broken');
    img.remove();
  });

  /* ---------------------------------------------------------------------
     1. Repeats
     --------------------------------------------------------------------- */

  // Index in `raw` where its first `len` normalized characters end.
  function cutIndex(raw, target) {
    var acc = '';
    for (var i = 0; i < raw.length; i++) {
      acc = norm(raw.slice(0, i + 1));
      if (acc.length >= target.length) return acc === target ? i + 1 : -1;
    }
    return -1;
  }

  body.querySelectorAll('.ss-bottom-line').forEach(function (line) {
    var t = norm(txt(line));
    if (t.length < 24) return;
    var next = line.nextElementSibling;
    if (!next) return;
    var opens = function (p) { return !p.classList.contains('ss-bottom-line') && norm(txt(p)).indexOf(t) === 0; };
    var direct = next.matches('p') && opens(next);
    if (!direct) {
      // The same sentence opening a paragraph further into the section (inside a
      // product card, under a sub-heading): the line is a stray copy, so it goes.
      for (var n = next; n && !n.matches('h2'); n = n.nextElementSibling) {
        var ps = n.matches('p') ? [n] : [].slice.call(n.querySelectorAll('p'));
        if (ps.some(opens)) { line.classList.add('ssa-dup'); return; }
      }
      return;
    }
    // The next paragraph opens with the same sentence. Keep the lede, fold the repeat.
    var p = next;
    var first = p.firstChild;
    if (first && first.nodeType === 3) {
      var at = cutIndex(first.nodeValue, t);
      if (at > 0) {
        var rest = first.nodeValue.slice(at);
        var lead = rest.match(/^[\s.;:,!?)\]"'”’]+/);
        if (lead) at += lead[0].length;
        var rep = el('span', 'ssa-repeat');
        rep.textContent = first.nodeValue.slice(0, at);
        first.nodeValue = first.nodeValue.slice(at);
        p.insertBefore(rep, first);
        if (!txt(p).replace(txt(rep), '').trim()) p.classList.add('ssa-repeat');
        return;
      }
    }
    line.classList.add('ssa-dup');
  });

  // The answer block. The header carries the one date, so a second "Last updated"
  // line goes. When the block is an answer followed by key points, only the
  // answer keeps the yellow: a full screen of yellow stops being a signal.
  body.querySelectorAll('.ss-tldr').forEach(function (box) {
    var label = box.querySelector(':scope > .ss-tldr-label');
    var inner = box.querySelector(':scope > .ss-tldr-content');   // some templates wrap the text once more
    var holder = inner || box;
    var kids = [].slice.call(holder.children);
    kids.forEach(function (k) {
      if (k !== label && /^\s*last updated/i.test(txt(k)) && txt(k).length < 60) k.classList.add('ssa-retired');
    });
    var rest = kids.filter(function (k) { return k !== label && !k.classList.contains('ssa-retired') && !k.matches('.ss-chips'); });
    var lead = rest[0];
    if (!lead || !lead.matches('p')) return;
    var after = rest.slice(1).filter(function (k) { return k.matches('ul, ol') || txt(k).length > 80; });
    if (!after.length) { lead.classList.add('ssa-lead'); return; }
    var answer = el('div', 'ssa-answer');
    box.insertBefore(answer, box.firstChild);
    if (label) answer.appendChild(label);
    answer.appendChild(lead);
    lead.classList.add('ssa-lead');
    box.classList.add('ssa-split');
  });

  // The same footnote printed again and again: the first one stays.
  var seenNotes = {};
  body.querySelectorAll('.ss-nsf-footnote, p.ss-text-small, p.ss-text-muted, .ss-footnote').forEach(function (n) {
    if (n.closest('.ss-buy-card, .ss-trust-strip, .ss-cta-banner, .ss-mid-cta, .ss-cta-header')) return;
    var k = norm(txt(n));
    if (k.length < 16) return;
    if (seenNotes[k]) n.classList.add('ssa-repeat'); else seenNotes[k] = true;
  });

  // The answer and the takeaways are set in regular weight; only the words a
  // skimming reader needs carry weight: figures with their units, product and
  // brand names, the standard. Words the author already made bold stay bold.
  var reEsc = function (s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
  var KEY = new RegExp('(' + [
    '\\$\\d+(?:[.,]\\d+)*(?:\\s?[-–]\\s?\\$?\\d+(?:[.,]\\d+)*)?',
    '\\d+(?:[.,]\\d+)*(?:\\s?[-–]\\s?\\d+(?:[.,]\\d+)*)?\\s?(?:%|ppm|GPM|micro-jets)',
    'Day\\s\\d+(?:\\s?[-–]\\s?\\d+)?',
    'NSF\\/ANSI\\s?\\d+',
    '(?:the\\s)?Second Shower(?:\\s(?:Showerhand|Showerhead|Head|Hand)\\b)?',
    'Showerhand\\b',
    'Vitamin C\\b'
  ].concat(Object.keys(BRANDS).sort(function (a, b) { return b.length - a.length; }).map(function (k) { return '\\b' + reEsc(k) + '\\b'; })).join('|') + ')', 'gi');

  function emphasize(node) {
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    var texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    texts.forEach(function (t) {
      if (t.parentElement.closest('strong, b, sup, .ssa-repeat')) return;
      var parts = t.nodeValue.split(KEY);
      if (parts.length < 3) return;
      var frag = document.createDocumentFragment();
      parts.forEach(function (part, i) {
        if (!part) return;
        if (i % 2) { var s = el('strong', 'ssa-key'); s.textContent = part; frag.appendChild(s); }
        else frag.appendChild(document.createTextNode(part));
      });
      t.parentNode.replaceChild(frag, t);
    });
  }
  // A rebuilt body (data-ss-body="v8") marks its own keywords; leave its weight alone.
  var authored = body.getAttribute('data-ss-body') === 'v8';
  if (!authored) body.querySelectorAll('.ss-tldr .ssa-lead, .ss-bottom-line:not(.ssa-dup)').forEach(emphasize);

  // Buy buttons the older templates left in the text: the frame carries the product now.
  body.querySelectorAll('a.ss-cta-button, a.ss-btn, a.ss-button, a.ss-cta, a.ss-cta-primary, a.ss-cta-secondary, a.ss-btn-primary, a.ss-btn-secondary, a.ss-product-cta, a.ss-cta-amazon').forEach(function (a) {
    var box = a.parentElement;
    while (box && box !== body && txt(box) === txt(a)) {
      if (box.parentElement === body) break;
      box = box.parentElement;
    }
    (box && box !== body && txt(box) === txt(a) ? box : a).classList.add('ssa-retired');
  });

  /* ---------------------------------------------------------------------
     2. Contents
     --------------------------------------------------------------------- */

  var visible = function (n) { return !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length); };
  var used = {};
  var short = {};
  body.querySelectorAll('.ss-jump a[href^="#"], .ss-jump-nav a[href^="#"], .ss-toc a[href^="#"], .ss-on-this-page a[href^="#"]').forEach(function (a) {
    short[a.getAttribute('href').slice(1)] = txt(a);
  });

  var heads = [].filter.call(body.querySelectorAll('h2'), visible).map(function (h) {
    if (!h.id) {
      var base = norm(txt(h)).replace(/ /g, '-').slice(0, 48) || 'section';
      var id = base, i = 2;
      while (document.getElementById(id) || used[id]) id = base + '-' + i++;
      h.id = id;
    }
    used[h.id] = true;
    return { id: h.id, node: h, label: short[h.id] || txt(h) };
  });

  var tocLists = [];
  function tocList() {
    var ol = el('ol', 'ssa-toc__list');
    heads.forEach(function (h) {
      ol.appendChild(el('li', '', '<a href="#' + h.id + '">' + esc(h.label) + '</a>'));
    });
    tocLists.push(ol);
    return ol;
  }

  var rail = root.querySelector('.ssa-rail');
  var hasToc = heads.length >= 3;

  if (hasToc) {
    if (rail) {
      var nav = el('nav', 'ssa-toc');
      nav.setAttribute('aria-label', 'In this article');
      nav.appendChild(el('p', 'ssa-rail__title', 'In this article'));
      nav.appendChild(el('div', 'ssa-rail__progress', '<span></span>'));
      nav.appendChild(tocList());
      rail.insertBefore(nav, rail.firstChild);
    }
    var answer = body.querySelector('.ss-tldr');
    var contents = el('details', 'ssa-contents');
    contents.appendChild(el('summary', '', '<span>In this article</span><span>' + heads.length + ' sections</span>'));
    var cnav = el('nav');
    cnav.setAttribute('aria-label', 'In this article');
    cnav.appendChild(tocList());
    contents.appendChild(cnav);
    // the scorecard, when there is one, stays attached to the answer
    if (answer && answer.nextElementSibling && answer.nextElementSibling.matches('.ss-scorecard')) answer = answer.nextElementSibling;
    // and so does the product line placed under it (above)
    if (answer && answer.nextElementSibling && answer.nextElementSibling.matches('.ssa-pick-inline')) answer = answer.nextElementSibling;
    if (answer && answer.parentElement === body) answer.insertAdjacentElement('afterend', contents);
    else body.insertBefore(contents, body.firstChild);
    contents.addEventListener('click', function (e) { if (e.target.closest('a')) contents.open = false; });
  }

  /* ---------------------------------------------------------------------
     3. Tables
     --------------------------------------------------------------------- */

  var PRODUCT_HEAD = /^(product|products|brand|brands|model|filter|filters|filter type|media|method|type|shower ?head|showerhead|name|option|pick)$/i;

  // Product pictures: ours only, from the theme (the live product image, so it
  // never goes stale). Rivals are text only.
  function imageFor(name) {
    if (!OURS.test(name)) return '';
    var hand = /showerhand|handheld|\bhand\b/i.test(name);
    var head = /showerhead|\bhead\b/i.test(name);
    var which = hand && !head ? 'hand' : head && !hand ? 'head' : (root.getAttribute('data-product') || 'head');
    return root.getAttribute('data-image-' + which) || '';
  }

  body.querySelectorAll('table').forEach(function (table) {
    var headRow = table.tHead && table.tHead.rows[0];
    var rows = [].slice.call(table.tBodies[0] ? table.tBodies[0].rows : []);
    if (!headRow && rows.length) { headRow = rows.shift(); }
    if (!headRow || !rows.length) return;
    var hc = [].slice.call(headRow.cells);
    var cols = hc.length;
    if (cols >= 5) {
      table.classList.add('ssa-is-wide');
      (table.closest('.ss-table-wrap') || table).classList.add('ssa-wide');
    }

    // Which way does it run? Our name in a column heading means one product per
    // column; our name at the start of a row means one product per row.
    var oursCols = hc.map(function (c, i) { return i > 0 && OURS.test(txt(c)); });
    var oursInHead = oursCols.some(Boolean);
    var oursInRows = rows.some(function (r) { return OURS.test(txt(r.cells[0])); });
    var rowsAreProducts = !oursInHead && (oursInRows || PRODUCT_HEAD.test(txt(hc[0])) || !!rows[0].cells[0].querySelector('.ss-th-cell, img'));

    var host = table.closest('.ss-table-wrap') || table;
    var allOurs = !rowsAreProducts && oursCols.filter(Boolean).length === cols - 1;   // Hand against Head

    // The products, in order, with what the table says about each.
    var headOf = function (cell) {
      var link = cell.querySelector('a[href]');
      return { cell: cell, name: txt(cell.querySelector('.ss-th-name') || cell), href: link ? link.getAttribute('href') : '' };
    };
    var products = [];
    if (rowsAreProducts) {
      rows.forEach(function (r) {
        var h = headOf(r.cells[0]);
        h.items = [];
        for (var i = 1; i < r.cells.length; i++) h.items.push({ label: txt(hc[i] || hc[0]), html: r.cells[i].innerHTML });
        h.row = r;
        products.push(h);
      });
    } else {
      for (var c = 1; c < cols; c++) {
        var h = headOf(hc[c]);
        h.items = rows.map(function (r) { return { label: txt(r.cells[0]), html: r.cells[c] ? r.cells[c].innerHTML : '' }; });
        h.col = c;
        products.push(h);
      }
    }
    products.forEach(function (p) {
      p.ours = OURS.test(p.name);
      p.img = imageFor(p.name);
      p.wordmark = WORDMARK && norm(p.name) === 'second shower';
    });

    // Ours carries its picture in a comparison; rivals are text only. Once the
    // table is a comparison, the loose picture grid older posts print above it
    // has no job left.
    var pictured = products.length >= 2 && products.some(function (p) { return p.ours && p.img; });
    var isComparison = pictured || products.some(function (p) { return p.ours; });
    if (isComparison) {
      var prev = host.previousElementSibling;
      while (prev && prev.matches('p') && txt(prev).length < 200) prev = prev.previousElementSibling;
      if (prev && prev.matches('.ss-compare-cards')) prev.classList.add('ssa-retired');
    }
    var nameHtml = function (p) {
      if (p.wordmark) return '<img class="ssa-wordmark" src="' + esc(WORDMARK) + '" alt="Second Shower">';
      return p.href ? '<a href="' + esc(p.href) + '" data-ss-cta="article_table">' + esc(p.name) + '</a>' : esc(p.name);
    };
    var tile = function (p) {
      if (!pictured || !p.ours || !p.img) return '';
      return '<span class="ssa-tile"><img src="' + esc(p.img) + '" alt="" loading="lazy"></span>';
    };

    // In the table itself: mark ours, and give every product the same head.
    if (rowsAreProducts) {
      products.forEach(function (p) {
        if (p.ours) p.row.classList.add('ssa-ours');
        if (pictured) p.cell.innerHTML = '<span class="ssa-th ssa-th--row">' + tile(p) + '<span class="ssa-th__name">' + nameHtml(p) + '</span></span>';
      });
    } else if (isComparison) {
      table.classList.add('ssa-vs');
      if (pictured) table.classList.add('ssa-vs--pictured');
      if (products.length === 2) table.classList.add('ssa-vs--pair');   // phones: label above, the two values side by side
      if (/^(attribute|attributes|feature|features|spec|specs|category|criteria|factor|metric|)$/i.test(txt(hc[0]))) hc[0].classList.add('ssa-quiet');
      products.forEach(function (p) {
        if (p.ours && !allOurs) {
          p.cell.classList.add('ssa-ours');
          rows.forEach(function (r) { if (r.cells[p.col]) r.cells[p.col].classList.add('ssa-ours'); });
        }
        // each value carries its row's name, for the phone layout that folds the first column away
        rows.forEach(function (r) { if (r.cells[p.col]) r.cells[p.col].setAttribute('data-label', txt(r.cells[0])); });
        if (pictured || p.wordmark) p.cell.innerHTML = '<span class="ssa-th">' + tile(p) + '<span class="ssa-th__name">' + nameHtml(p) + '</span></span>';
      });
    }

    // Phones: a comparison with four or more columns becomes labelled cards.
    if (cols < 4 || products.length < 2) return;
    var maxRows = Math.max.apply(null, products.map(function (p) { return p.items.length; }));
    var always = cols >= 7;
    var wrap = el('div', 'ssa-cards' + (always ? ' ssa-cards--always' : ''));
    wrap.style.setProperty('--ssa-rows', String(maxRows + 1));
    wrap.setAttribute('role', 'list');
    wrap.setAttribute('aria-label', 'Comparison, one card per product');
    wrap.tabIndex = 0;
    products.forEach(function (p, idx) {
      var card = el('div', 'ssa-card' + (p.ours && !allOurs ? ' ssa-card--ours' : ''));
      card.setAttribute('role', 'listitem');
      card.appendChild(el('div', 'ssa-card__head', tile(p) + '<div><span class="ssa-card__name">' + nameHtml(p) + '</span><span class="ssa-card__n">' + (idx + 1) + ' of ' + products.length + '</span></div>'));
      for (var i = 0; i < maxRows; i++) {
        var it = p.items[i];
        card.appendChild(el('div', 'ssa-card__row', it ? '<span class="ssa-card__label">' + esc(it.label) + '</span><span class="ssa-card__value">' + it.html + '</span>' : ''));
      }
      wrap.appendChild(card);
    });
    host.classList.add('ssa-has-cards');
    if (always) host.classList.add('ssa-has-cards-always');
    host.insertAdjacentElement('afterend', wrap);

    // Under the cards: our own position bar (it starts and ends on the text
    // margins, which a native scrollbar on a full-bleed rail cannot do) and,
    // where there is a pointer rather than a thumb, two buttons to step through.
    var arrow = function (d) { return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + d + '"/></svg>'; };
    var nav = el('div', 'ssa-cards__nav' + (always ? ' ssa-cards__nav--always' : ''));
    var bar = el('div', 'ssa-cards__bar', '<span></span>');
    bar.setAttribute('aria-hidden', 'true');
    var prevBtn = el('button', 'ssa-cards__step', arrow('m15 6-6 6 6 6'));
    var nextBtn = el('button', 'ssa-cards__step', arrow('m9 6 6 6-6 6'));
    prevBtn.type = nextBtn.type = 'button';
    prevBtn.setAttribute('aria-label', 'Previous product');
    nextBtn.setAttribute('aria-label', 'Next product');
    nav.appendChild(bar);
    nav.appendChild(prevBtn);
    nav.appendChild(nextBtn);
    wrap.insertAdjacentElement('afterend', nav);
    var thumb = bar.firstChild;
    var place = function () {
      var max = wrap.scrollWidth - wrap.clientWidth;
      var share = wrap.scrollWidth ? wrap.clientWidth / wrap.scrollWidth : 1;
      thumb.style.width = (share * 100).toFixed(2) + '%';
      thumb.style.left = (max > 0 ? (wrap.scrollLeft / max) * (1 - share) * 100 : 0).toFixed(2) + '%';
      nav.hidden = max <= 0;
      prevBtn.disabled = wrap.scrollLeft <= 1;
      nextBtn.disabled = wrap.scrollLeft >= max - 1;
      // the right-edge fade (ss-article.css): only while there are cards left to the right
      wrap.classList.toggle('is-more-end', max > 0 && wrap.scrollLeft < max - 1);
    };
    var step = function (dir) {
      var card = wrap.querySelector('.ssa-card');
      var by = card ? card.getBoundingClientRect().width + 12 : wrap.clientWidth * 0.8;
      wrap.scrollBy({ left: dir * by, behavior: calm ? 'auto' : 'smooth' });
    };
    prevBtn.addEventListener('click', function () { step(-1); });
    nextBtn.addEventListener('click', function () { step(1); });
    wrap.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place, { passive: true });
    place();
  });

  // A picture that fails to load takes its frame with it, so no empty tile is left.
  var dropBroken = function (img) {
    var frame = img.closest('.ssa-tile, .ss-th-img, .ss-dive-media, .ss-compare-card, figure');
    (frame || img).classList.add('ssa-broken');
  };
  body.querySelectorAll('img').forEach(function (img) {
    if (img.loading !== 'lazy' && img.complete && img.naturalWidth === 0 && img.getAttribute('src')) dropBroken(img);
    else img.addEventListener('error', function () { dropBroken(img); });
  });

  /* ---------------------------------------------------------------------
     4. FAQ
     --------------------------------------------------------------------- */

  function toDetails(q, parts) {
    var d = el('details', 'ss-faq-item');
    var s = el('summary');
    s.innerHTML = q.innerHTML;
    var a = el('div', 'ss-faq-a');
    d.appendChild(s);
    d.appendChild(a);
    q.parentNode.insertBefore(d, q);
    parts.forEach(function (p) { a.appendChild(p); });
    q.remove();
    return d;
  }

  body.querySelectorAll('div.ss-faq-item').forEach(function (item) {
    var q = item.querySelector(':scope > h3, :scope > h4, :scope > strong, :scope > .ss-faq-q');
    if (!q) return;
    var parts = [].filter.call(item.children, function (n) { return n !== q; });
    var d = toDetails(q, parts);
    item.replaceWith(d);
  });

  body.querySelectorAll('.ss-faq, .ss-faq-section').forEach(function (faq) {
    [].slice.call(faq.querySelectorAll(':scope > h3')).forEach(function (q) {
      var parts = [];
      var n = q.nextElementSibling;
      while (n && !n.matches('h2, h3, details, div.ss-faq-item')) { parts.push(n); n = n.nextElementSibling; }
      if (parts.length) toDetails(q, parts);
    });
  });

  // one answer open at a time
  var faqs = [].slice.call(body.querySelectorAll('details.ss-faq-item'));
  faqs.forEach(function (d) {
    d.addEventListener('toggle', function () {
      if (!d.open) return;
      faqs.forEach(function (o) { if (o !== d) o.open = false; });
    });
  });

  /* ---------------------------------------------------------------------
     5. Dock, sheet, progress, current section
     --------------------------------------------------------------------- */

  var dock = document.querySelector('.ssa-dock');
  var sheet = null;
  var lastFocus = null;

  function closeSheet() {
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    behind().forEach(function (n) { n.removeAttribute('inert'); });
    if (lastFocus) lastFocus.focus();
  }

  // Everything the open sheet covers: the page's own top-level blocks, the
  // article and the dock. Made inert while the sheet is open, so neither Tab
  // nor a screen reader can wander behind it.
  function behind() {
    return [].filter.call(document.body.children, function (n) { return n !== sheet && n.tagName !== 'SCRIPT' && n.tagName !== 'STYLE'; });
  }

  if (dock && hasToc) {
    var btn = el('button', 'ssa-dock__toc',
      '<svg class="ssa-dock__ring" viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="20"/></svg>' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M5 7h14M5 12h14M5 17h9"/></svg>');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'In this article');
    btn.setAttribute('aria-haspopup', 'dialog');
    dock.insertBefore(btn, dock.firstChild);

    sheet = el('div', 'ssa-sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'In this article');
    var backdrop = el('button', 'ssa-sheet__backdrop');
    backdrop.type = 'button';
    backdrop.setAttribute('aria-label', 'Close');
    backdrop.tabIndex = -1;
    var panel = el('div', 'ssa-sheet__panel');
    var head = el('div', 'ssa-sheet__head', '<p class="ssa-sheet__title">In this article</p>');
    var close = el('button', 'ssa-sheet__close', '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    head.appendChild(close);
    panel.appendChild(head);
    panel.appendChild(tocList());
    sheet.appendChild(backdrop);
    sheet.appendChild(panel);
    document.body.appendChild(sheet);

    btn.addEventListener('click', function () {
      lastFocus = btn;
      sheet.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      behind().forEach(function (n) { n.setAttribute('inert', ''); });
      close.focus();
    });
    // Tab stays inside the sheet, wrapping at both ends (for browsers without inert).
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var stops = panel.querySelectorAll('a[href], button');
      var first = stops[0], last = stops[stops.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    backdrop.addEventListener('click', closeSheet);
    close.addEventListener('click', closeSheet);
    panel.addEventListener('click', function (e) { if (e.target.closest('a')) { lastFocus = null; closeSheet(); } });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSheet(); });
  }

  // In-page links (contents, sheet, the article's own "#section" links): we do
  // the scroll. The heading then lands under the floating site header (its
  // scroll-margin), the address keeps the section, and the link also works on a
  // page whose base URL is not its own address.
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a || !a.closest('.ssa, .ssa-sheet')) return;
    var id = a.getAttribute('href').slice(1);
    try { id = decodeURIComponent(id); } catch (err) { /* keep the raw id */ }
    var target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    if (a.closest('.ssa-sheet')) { lastFocus = null; closeSheet(); }
    var box = a.closest('.ssa-contents');
    if (box) box.open = false;
    requestAnimationFrame(function () {
      target.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
      // keyboard and screen-reader focus follows the reader to the section
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      try { history.replaceState(null, '', location.href.split('#')[0] + '#' + id); } catch (err) { /* address stays as it is */ }
    });
  });

  var closeMod = root.querySelector('.ssa-close');
  var ticking = false;
  var current = null;

  function update() {
    ticking = false;
    var vh = window.innerHeight;
    var r = body.getBoundingClientRect();
    var total = Math.max(1, r.height - vh * 0.6);
    var p = Math.max(0, Math.min(100, (-r.top / total) * 100));
    root.style.setProperty('--ssa-progress', p.toFixed(1));
    if (dock) {
      dock.style.setProperty('--ssa-progress', p.toFixed(1));
      var past = window.scrollY > vh * 0.6;
      var atClose = closeMod && closeMod.getBoundingClientRect().top < vh - 80 && closeMod.getBoundingClientRect().bottom > 0;
      var beyond = r.bottom < -vh * 0.5;
      dock.classList.toggle('is-visible', past && !atClose && !beyond);
    }
    if (hasToc) {
      var now = null;
      for (var i = 0; i < heads.length; i++) {
        if (heads[i].node.getBoundingClientRect().top <= 140) now = heads[i].id; else break;
      }
      if (now !== current) {
        current = now;
        tocLists.forEach(function (ol) {
          ol.querySelectorAll('a').forEach(function (a) {
            if (a.getAttribute('href') === '#' + now) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
          });
        });
      }
    }
  }

  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();
